import type { ComplianceSummary } from "../types";
import { windowShort } from "../lib/reportingWindow";

interface Props {
  summary: ComplianceSummary;
}

const TARGET = 80;

function weeklyBarColor(pct: number): string {
  if (pct >= TARGET) return "bg-emerald-500";
  if (pct >= TARGET - 10) return "bg-amber-400";
  return "bg-red-500";
}

function monthEndBarColor(pct: number): string {
  if (pct >= TARGET) return "bg-emerald-600";
  if (pct >= TARGET - 10) return "bg-amber-500";
  return "bg-red-700";
}

function approvalBarColor(pct: number): string {
  if (pct >= TARGET) return "bg-rancho-orange";
  if (pct >= TARGET - 10) return "bg-amber-400";
  return "bg-red-500";
}

function pctText(pct: number): string {
  if (pct >= TARGET) return "text-emerald-700";
  if (pct >= TARGET - 10) return "text-amber-700";
  return "text-red-700";
}

export default function MonthEndChart({ summary }: Props) {
  const monthly = (summary.monthly_compliance ?? []).map((m) => ({
    ...m,
    weekly_pct: m.weekly_pct ?? 0,
    weekly_on_time: m.weekly_on_time ?? 0,
    weekly_total: m.weekly_total ?? 0,
    month_end_pct:
      m.month_end_pct !== undefined && m.month_end_pct !== null
        ? m.month_end_pct
        : (typeof m.pct === "number" ? m.pct : null),
    month_end_passed: m.month_end_passed ?? m.passed ?? 0,
    month_end_evaluated: m.month_end_evaluated ?? m.evaluated ?? 0,
    month_end_missed: m.month_end_missed ?? m.missed ?? 0,
    approval_pct: m.approval_pct ?? null,
    approval_passed: m.approval_passed ?? 0,
    approval_evaluated: m.approval_evaluated ?? 0,
    approval_missed: m.approval_missed ?? 0,
    hours_pct: m.hours_pct ?? null,
    hours_passed: m.hours_passed ?? 0,
    hours_evaluated: m.hours_evaluated ?? 0,
  }));
  if (monthly.length === 0) return null;

  const totalWkOnTime = monthly.reduce((acc, m) => acc + m.weekly_on_time, 0);
  const totalWkTotal = monthly.reduce((acc, m) => acc + m.weekly_total, 0);
  const ytdWeekly = totalWkTotal > 0
    ? Math.round((1000 * totalWkOnTime) / totalWkTotal) / 10
    : 0;

  const totalMePassed = monthly.reduce((acc, m) => acc + m.month_end_passed, 0);
  const totalMeEvaluated = monthly.reduce((acc, m) => acc + m.month_end_evaluated, 0);
  const ytdMonthEnd = totalMeEvaluated > 0
    ? Math.round((1000 * totalMePassed) / totalMeEvaluated) / 10
    : 0;

  const apprMonths = monthly.filter((m) => m.approval_pct !== null);
  const totalApprPassed = apprMonths.reduce((acc, m) => acc + m.approval_passed, 0);
  const totalApprEvaluated = apprMonths.reduce((acc, m) => acc + m.approval_evaluated, 0);
  const ytdApproval = totalApprEvaluated > 0
    ? Math.round((1000 * totalApprPassed) / totalApprEvaluated) / 10
    : 0;
  const hasApproval = apprMonths.length > 0;

  const hoursMonths = monthly.filter((m) => m.hours_pct !== null);
  const totalHoursPassed = hoursMonths.reduce((acc, m) => acc + m.hours_passed, 0);
  const totalHoursEvaluated = hoursMonths.reduce((acc, m) => acc + m.hours_evaluated, 0);
  const ytdHours = totalHoursEvaluated > 0
    ? Math.round((1000 * totalHoursPassed) / totalHoursEvaluated) / 10
    : 0;
  const hasHours = hoursMonths.length > 0;

  return (
    <section className="rounded-2xl bg-white shadow-card border border-slate-200/70 overflow-hidden">
      <header className="px-5 py-4 border-b border-slate-100 flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-base font-bold tracking-tight text-rancho-ink">
            Compliance trend
          </h2>
          <p className="text-xs text-rancho-slate mt-0.5">
            Per month: weekly submission %, month-end submission %,
            team-lead approval % (Rancho-billed, due by the 2nd working day
            of the following month), and hours worked % (allotted weekly
            hours logged). Dashed line marks the {TARGET}% target.
          </p>
        </div>
        <div className="flex items-center gap-6 text-right">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-rancho-slate">
              Weekly · {windowShort(summary)}
            </div>
            <div className={`text-xl font-extrabold ${pctText(ytdWeekly)}`}>
              {ytdWeekly.toFixed(1)}%
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-rancho-slate">
              Month-end · {windowShort(summary)}
            </div>
            <div className={`text-xl font-extrabold ${pctText(ytdMonthEnd)}`}>
              {ytdMonthEnd.toFixed(1)}%
            </div>
          </div>
          {hasApproval && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-rancho-slate">
                Approval · {windowShort(summary)}
              </div>
              <div className={`text-xl font-extrabold ${pctText(ytdApproval)}`}>
                {ytdApproval.toFixed(1)}%
              </div>
            </div>
          )}
          {hasHours && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-rancho-slate">
                Hours · {windowShort(summary)}
              </div>
              <div className={`text-xl font-extrabold ${pctText(ytdHours)}`}>
                {ytdHours.toFixed(1)}%
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="px-5 pt-6 pb-4">
        <div className="flex items-end gap-6">
          {monthly.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
              {/* Plot area: the full height represents 0-100%, so the bars
                  and the dashed target line share a single scale. */}
              <div className="relative w-full max-w-[140px] h-36 bg-slate-100 rounded-t-md">
                {/* 80% target line */}
                <div
                  className="absolute left-0 right-0 border-t-2 border-dashed border-slate-700/70 pointer-events-none z-20"
                  style={{ bottom: `${TARGET}%` }}
                  aria-hidden
                />
                {/* Bars, anchored to the floor of the plot area */}
                <div className="absolute inset-0 flex items-end gap-1">
                  <Bar
                    pct={m.weekly_pct}
                    color={weeklyBarColor(m.weekly_pct)}
                    tooltip={`Weekly: ${m.weekly_on_time}/${m.weekly_total} (${m.weekly_pct}%) - target ${TARGET}%`}
                  />
                  <Bar
                    pct={m.month_end_pct}
                    color={
                      m.month_end_pct !== null
                        ? monthEndBarColor(m.month_end_pct)
                        : "bg-slate-300"
                    }
                    tooltip={
                      m.month_end_pct === null
                        ? "No month-end check (month ended on Sunday)"
                        : `Month-end submission: ${m.month_end_passed}/${m.month_end_evaluated} (${m.month_end_pct}%) - target ${TARGET}%`
                    }
                  />
                  <Bar
                    pct={m.approval_pct}
                    color={
                      m.approval_pct !== null
                        ? approvalBarColor(m.approval_pct)
                        : "bg-slate-300"
                    }
                    tooltip={
                      m.approval_pct === null
                        ? "Approval deadline (2nd working day of next month) has not passed yet"
                        : `Team-lead approval: ${m.approval_passed}/${m.approval_evaluated} person-months on time (${m.approval_pct}%) - target ${TARGET}%`
                    }
                  />
                  <Bar
                    pct={m.hours_pct}
                    color={
                      m.hours_pct !== null
                        ? weeklyBarColor(m.hours_pct)
                        : "bg-slate-300"
                    }
                    tooltip={
                      m.hours_pct === null
                        ? "Hours pillar not evaluable for this month (no schedule data)"
                        : `Hours worked: ${m.hours_passed}/${m.hours_evaluated} person-weeks met their allotted hours (${m.hours_pct}%) - target ${TARGET}%`
                    }
                  />
                </div>
              </div>
              {/* Axis labels sit below the plot so they can't shift the bar
                  baseline out of sync with the target line. */}
              <div className="flex w-full max-w-[140px] gap-1">
                <span className="flex-1 text-center text-[9px] text-rancho-slate font-semibold">Wk</span>
                <span className="flex-1 text-center text-[9px] text-rancho-slate font-semibold">ME</span>
                <span className="flex-1 text-center text-[9px] text-rancho-slate font-semibold">Appr</span>
                <span className="flex-1 text-center text-[9px] text-rancho-slate font-semibold">Hrs</span>
              </div>
              <div className="text-[11px] font-semibold text-rancho-ink">{m.label}</div>
            </div>
          ))}
        </div>
        <Legend />
      </div>
    </section>
  );
}

function Bar({
  pct,
  color,
  tooltip,
}: {
  pct: number | null;
  color: string;
  tooltip: string;
}) {
  const visiblePct = pct === null ? 0 : Math.max(2, pct);
  return (
    <div className="relative flex-1 h-full flex items-end" title={tooltip}>
      <div
        className={`w-full ${color} rounded-t-sm transition-all`}
        style={{ height: `${visiblePct}%` }}
      />
      {/* Value label floats just above the bar top; absolute so it never
          shifts the bar's baseline relative to the target line. */}
      <div
        className={`absolute left-0 right-0 text-center text-[10px] font-bold leading-none ${
          pct !== null ? pctText(pct) : "text-rancho-slate"
        }`}
        style={{ bottom: `calc(${visiblePct}% + 2px)` }}
      >
        {pct === null ? "-" : `${pct.toFixed(0)}%`}
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="mt-4 flex items-center justify-center gap-x-6 gap-y-2 text-xs flex-wrap" style={{ color: "#475569" }}>
      <span className="font-semibold" style={{ color: "#111827" }}>
        Bars: Wk = weekly · ME = month-end · Appr = approval · Hrs = hours worked
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block w-4 h-3 rounded-sm"
          style={{ backgroundColor: "#10B981" }}
        />
        On target (≥{TARGET}%)
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block w-4 h-3 rounded-sm"
          style={{ backgroundColor: "#F59E0B" }}
        />
        Close ({TARGET - 10}–{TARGET - 1}%)
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block w-4 h-3 rounded-sm"
          style={{ backgroundColor: "#EF4444" }}
        />
        Below (&lt;{TARGET - 10}%)
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-6 border-t-2 border-dashed" style={{ borderColor: "rgba(15,23,42,0.6)" }} />
        {TARGET}% target
      </span>
    </div>
  );
}
